import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { tap } from 'rxjs/operators';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

import { AlertService } from '@shared/services/alert.service';
import { HarpValidators } from '@shared/utils/validators';
import { InputConfig } from '@shared/models/input-config.model';
import { TEXT_CONSTANTS } from '@shared/constants';
import { SpinnerService } from '@shared/services/spinner.service';
import { WorkflowService } from '@shared/components/routeGuard/workflow.service';
import { UserProfile } from '@login/models/user-profile.model';
import { constant } from '@login/resources/constant';
import { HarpLoginService } from '@login/services/harp-login.service';
import { NavigationService } from '@login/services/navigation.service';

import { FactorService } from '@login/services/factor.service';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { ResendCodeTimerService } from '@login/services/resend-code-timer.service';
import { TitleCasePipe } from '@angular/common';
const {
  loginSuccessStatus: LOGIN_SUCCESS_STATUS,
  OKTA_VERIFY_PUSH_TIMEOUT,
  USR_FACT_CHK_CALL_FAILURE_1,
} = constant;

const OKTA_VERIFY_REDIRECT_TIMEOUT = 5000;

@Component({
  selector: 'app-mfa',
  templateUrl: './mfa.component.html',
  styleUrls: ['./mfa.component.scss'],
})
export class MfaComponent implements OnInit {
  isCodeSent = false;
  mfaInfo = new UserProfile();

  buttonText = 'Send Code';

  isResendDisabled = false;
  resendCodeTimer$;
  resendCodeTimeRemaining;
  showResendCodeAlert;
  showResendCodeSuccessAlert;
  isSendCodeDisabled = false;

  deviceTypeFactorTypeMap = {
    SMS_FACTOR: 'Text',
    EMAIL_FACTOR: 'email',
    SOFT_TOKEN: 'Google Authenticator',
    CALL_FACTOR: 'Call',
    OKTA_SOFT_TOKEN: 'Okta Verify',
    OKTA_VERIFY_PUSH: 'Okta Verify Push',
  };

  factorTypeButtonTextMap = {
    'Okta Verify': 'Enter Code',
    'Google Authenticator': 'Enter Code',
    'Okta Verify Push': 'Send Push',
  };

  otpFormConfig: UntypedFormGroup;
  otpInputConfig: InputConfig;

  basicAuthForm: UntypedFormGroup;
  userIdConfig: InputConfig;
  passwordConfig: InputConfig;

  mfaSelectForm: UntypedFormGroup;
  mfaSelectInputConfig: InputConfig;

  repromptUsernamePwd = false;
  oktaVerifyPushSent = false;
  isMfaDisabled = false;

  factorCheckSubscription: Subscription;
  private selectedFactor = null;

  titleCasePipe = new TitleCasePipe();

  constructor(
    private router: Router,
    private harpLoginService: HarpLoginService,
    public workflowService: WorkflowService,
    private navigationService: NavigationService,
    private spinnerService: SpinnerService,
    private alertService: AlertService,
    private factorService: FactorService,
    private fb: UntypedFormBuilder,
    private resendCodeTimerService: ResendCodeTimerService,
    private modalService: NgbModal,
  ) {}

  ngOnInit() {
    this.resendCodeTimer$ = this.resendCodeTimerService
      .getResendCodeTimer()
      .pipe(
        tap(t => {
          if (t === 0) {
            this.isResendDisabled = false;
            this.showResendCodeAlert = false;
            this.showResendCodeSuccessAlert = false;
            this.isSendCodeDisabled = false;
          }
          this.resendCodeTimeRemaining = t;
        }),
      );

    this.mfaInfo = this.harpLoginService.getUserProfile();
    this.initForm();

    this.setMfaDevice();
  }

  private initForm() {
    this.initOtpForm();
    this.initBasicAuthForm();
    this.initMfaSelectForm();
  }

  private initOtpForm() {
    this.otpFormConfig = this.fb.group({
      otp: [
        '',
        [
          HarpValidators.required(
            TEXT_CONSTANTS.ERROR_MESSAGE_REQUIRED('Security Code'),
          ),
          HarpValidators.pattern('^[0-9]*$', 'Invalid Security Code'),
        ],
      ],
    });

    this.otpInputConfig = {
      name: 'otp',
      label: 'Security Code',
      required: true,
      form: this.otpFormConfig,
      autofocus: true,
    };
  }

  private initBasicAuthForm() {
    this.basicAuthForm = this.fb.group({
      userId: [
        '',
        HarpValidators.required(
          TEXT_CONSTANTS.ERROR_MESSAGE_REQUIRED('User ID'),
        ),
        ,
      ],
      password: [
        '',
        HarpValidators.required(
          TEXT_CONSTANTS.ERROR_MESSAGE_REQUIRED('Password'),
        ),
      ],
    });

    this.userIdConfig = {
      name: 'userId',
      label: 'User ID',
      maxlength: 100,
      required: true,
      form: this.basicAuthForm,
    };

    this.passwordConfig = {
      name: 'password',
      label: 'Password',
      type: 'password',
      required: true,
      form: this.basicAuthForm,
    };
  }

  private initMfaSelectForm() {
    this.mfaSelectForm = this.fb.group({
      mfa: [, [HarpValidators.required('')]],
    });

    this.mfaSelectForm.get('mfa').valueChanges.subscribe({
      next: factor => {
        this.cancelFactorCheck();

        this.isSendCodeDisabled = false;
        this.showResendCodeAlert = false;

        this.setButtonText();
        this.selectedFactor = factor;
      },
    });

    this.mfaSelectInputConfig = {
      form: this.mfaSelectForm,
      type: 'select',
      name: 'mfa',
      label: 'Device',
      required: true,
      noNullOption: true,
      options: this.mfaInfo.mfaList.map(factor => ({
        value: factor,
        label: this.titleCasePipe.transform(factor?.factorType),
      })),
    };
  }

  showPhoneModalLinks = (): boolean => {
    return (
      this.selectedFactor &&
      (this.selectedFactor.factorType.includes('Text') ||
        this.selectedFactor.factorType.includes('Call'))
    );
  };

  openModal(modalName) {
    this.modalService.open(modalName, {
      size: 'lg',
    });
  }

  setMfaDevice() {
    this.otpFormConfig.reset();
    this.getLastDeviceUsed(this.mfaInfo.mfaList);
  }

  getLastDeviceUsed(mfaList) {
    this.spinnerService.start();
    this.setSelectedFactor(mfaList[0]?.factorType);
    this.setButtonText();
    this.spinnerService.stop();

    if (
      mfaList[0]?.factorType === this.deviceTypeFactorTypeMap.OKTA_VERIFY_PUSH
    ) {
      this.userFactorCheck();
    }
  }

  setSelectedFactor(deviceType) {
    if (!deviceType) {
      return;
    }

    if (deviceType?.includes('Text')) {
      deviceType = 'Text';
    }

    if (deviceType?.includes('Call')) {
      deviceType = 'Call';
    }

    switch (deviceType) {
      case 'Text':
        deviceType = 'SMS_FACTOR';
        break;
      case 'email':
        deviceType = 'EMAIL_FACTOR';
        break;
      case 'Google Authenticator':
        deviceType = 'SOFT_TOKEN';
        break;
      case 'Call':
        deviceType = 'CALL_FACTOR';
        break;
      case 'Okta Verify':
        deviceType = 'OKTA_SOFT_TOKEN';
        break;
      case 'Okta Verify Push':
        deviceType = 'OKTA_VERIFY_PUSH';
        break;
    }
    const factorType = this.deviceTypeFactorTypeMap[deviceType];

    if (!factorType) {
      return;
    }

    const factor = this.mfaInfo.mfaList.find(mfa =>
      mfa.factorType.includes(factorType),
    );

    if (!factor) {
      return;
    }

    this.mfaSelectForm.get('mfa').setValue(factor);
  }

  getLastDeviceUsedFail() {
    this.spinnerService.stop();
  }

  resetResendCodeTimer() {
    this.resendCodeTimerService.getResetResendCodeTimer().next();
  }

  setButtonText() {
    this.buttonText =
      this.factorTypeButtonTextMap[this.factorType] || 'Send Code';
  }

  saveMfaInformation(mfaInformationForm) {
    this.harpLoginService.setUserProfile(this.mfaInfo);
  }

  private cancelFactorCheck() {
    this.factorCheckSubscription?.unsubscribe?.();
  }

  resendCode() {
    this.alertService.clearAlert();
    this.isResendDisabled = true;

    if (this.resendCodeTimeRemaining > 0) {
      this.showResendCodeAlert = true;
      return;
    }

    this.harpLoginService
      .resendCode({
        factorid: this.factorId,
        factorType: this.factorType,
      })
      .subscribe((res: any) => {
        switch (res.authstatus) {
          case constant.invalidSession:
            this.navigationService.goToLoginScreen();
            break;
          case constant.tooManyRequests:
            this.showResendCodeAlert = true;
            this.isResendDisabled = true;

            this.alertService.setAlert(constant.TOO_MANY_REQUESTS_ERROR_MSG);
            break;
          case constant.factorSuccessStatus:
            this.showResendCodeSuccessAlert = true;
            this.showResendCodeAlert = false;
            this.isCodeSent = true;
            this.resetResendCodeTimer();
            break;
          default:
            this.isResendDisabled = false;
            this.alertService.setAlert(constant.USR_FACT_CHK_CALL_FAILURE_0);
            break;
        }
      });
  }

  userFactorCheck(event?: any) {
    console.log('USER FACTOR');
    this.cancelFactorCheck();

    if (this.factorType === 'Okta Verify Push') {
      this.oktaVerifyPushSent = true;
      this.buttonText = 'Push Sent';
      this.isSendCodeDisabled = true;
      if (event?.target) {
        event.target.disabled = true;
      }
    }

    this.factorCheckSubscription = this.harpLoginService
      .factorCheck({
        factorid: this.factorId,
        factorType: this.factorType,
      })
      .subscribe(
        (httpResponse: any) => {
          if (!httpResponse) {
            this.alertService.setAlert(constant.USR_FACT_CHK_CALL_FAILURE_0);
            return;
          }

          if (httpResponse.authstatus === constant.tooManyRequests) {
            this.showResendCodeAlert = true;
            this.isSendCodeDisabled = true;
          } else if (httpResponse.authstatus === constant.factorSuccessStatus) {
            this.isCodeSent = true;

            if (this.isTextOrCall) {
              this.resetResendCodeTimer();
            }
          } else {
            this.harpLoginService.authServiceSuccessCallback(httpResponse);
          }
        },
        (error: any) => {
          if (error.status === 504 && this.factorType === 'Okta Verify Push') {
            this.isMfaDisabled = true;
            this.alertService.setAlert(OKTA_VERIFY_PUSH_TIMEOUT);
            setTimeout(
              () => this.navigationService.goToLoginScreen(),
              OKTA_VERIFY_REDIRECT_TIMEOUT,
            );
          } else {
            this.alertService.setAlert(USR_FACT_CHK_CALL_FAILURE_1);
          }
        },
      );
  }

  sendCodeCheck() {
    const { otp: mfaCode } = this.otpFormConfig.value;
    this.harpLoginService
      .factorCheck({
        mfaCode,
      })
      .subscribe(
        (httpResponse: any) => this.sendCodeCheckSuccess(httpResponse),
        () => this.sendCodeCheckError(),
      );
  }

  sendCodeCheckSuccess(httpResponse) {
    console.log('CODE CHECK');
    if (!httpResponse) {
      this.alertService.setAlert(constant.SEND_CODE_CHK_CALL_FAILURE_0);
      return;
    }

    if (httpResponse.authstatus === constant.mfaSuccessStatus) {
      this.isCodeSent = true;
    }

    this.harpLoginService.authServiceSuccessCallback(httpResponse);
  }

  sendCodeCheckError() {
    this.alertService.setAlert(constant.SEND_CODE_CHK_CALL_FAILURE_1);
  }

  resubmitBasicAuth() {
    const { userId, password } = this.basicAuthForm.value;

    this.harpLoginService
      .checkLoginStatusAndRedirect({
        userId,
        password,
      })
      .subscribe({
        next: res => {
          if (res === LOGIN_SUCCESS_STATUS) {
            this.router.navigate(['/login/mfa-challenge'], {
              queryParamsHandling: 'preserve',
            });
          }
        },
      });
  }

  get factorControl() {
    return this.mfaSelectForm.get('mfa');
  }

  get factorType() {
    return this.factorControl?.value?.factorType;
  }

  get factorId() {
    return this.factorControl?.value?.id;
  }

  get isTextOrCall() {
    return ['Call', 'Text'].some(type => this.factorType.includes(type));
  }
}
